/**
 * LeagueAnnouncementsPage — /league/:leagueId/announcements
 * ─────────────────────────────────────────────────────────────────
 * Phase 8 of the League Module. Per-league announcement feed.
 *
 * Visible to any league member. Compose/edit/retract restricted
 * to users with `manage_announcements` (managers always, admins
 * if granted). The frontend renders the compose box only for
 * permitted users; the backend re-checks every write so a player
 * manually crafting a POST gets a clean 403.
 *
 * Display rules:
 *   • Pinned-and-unexpired announcements float to top.
 *   • Severity drives badge color: info (neutral), important
 *     (cyan accent), urgent (red highlight).
 *   • A "Dismiss" button records the per-user dismissal so the
 *     unread badge clears without retracting the post for others.
 *   • Manager-only actions (edit / retract) only render when the
 *     user has the permission.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Megaphone, Pin, ExternalLink,
  Pencil, Trash2, X, Plus, Check,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';

export default function LeagueAnnouncementsPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canPost = isManager || can('manage_announcements');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [includeRetracted, setIncludeRetracted] = useState(false);

  // Initial load + reload after writes.
  async function load() {
    setLoading(true);
    try {
      const params = includeRetracted ? '?include_retracted=true' : '';
      const { data } = await api.get(`/leagues/${leagueId}/announcements${params}`);
      setItems(data?.announcements || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load announcements');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId, includeRetracted]);

  async function handleDismiss(id) {
    try {
      await api.post(`/leagues/${leagueId}/announcements/${id}/dismiss`);
      setItems(prev => prev.map(a => a.id === id ? { ...a, is_dismissed: true } : a));
    } catch {
      toast.error('Could not dismiss');
    }
  }

  async function handleRetract(id) {
    if (!window.confirm('Retract this announcement? It will be hidden from members.')) return;
    try {
      await api.delete(`/leagues/${leagueId}/announcements/${id}`);
      toast.success('Retracted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not retract');
    }
  }

  if (roleLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#7a82a0' }}>Loading…</div>;
  }

  return (
    <div style={{ paddingBottom: 60 }}>
      <LeagueSubNav league={null /* same as every other league page; SubNav uses useParams for leagueId */} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Megaphone size={22} /> Announcements
          </h1>
          {canPost && (
            <button onClick={() => { setComposing(true); setEditing(null); }} style={primaryBtn}>
              <Plus size={16} /> New
            </button>
          )}
        </header>

        {composing && (
          <ComposeCard
            // v2.5.123 audit (round 8) — key forces remount when
            // switching between "new" and "edit X" or between
            // editing X vs editing Y. Without it, the useState
            // initializers don't rerun and the form shows stale
            // data from the previous compose session.
            key={editing?.id || '__new__'}
            leagueId={leagueId}
            existing={editing}
            onClose={() => { setComposing(false); setEditing(null); }}
            onSaved={() => { setComposing(false); setEditing(null); load(); }}
          />
        )}

        {canPost && (
          <label style={{ fontSize: 12, color: '#7a82a0', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeRetracted}
                   onChange={(e) => setIncludeRetracted(e.target.checked)} />
            Show retracted
          </label>
        )}

        {loading && <p style={muted}>Loading…</p>}
        {!loading && items.length === 0 && (
          <p style={muted}>No announcements yet.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(a => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              canEdit={canPost && !a.retracted_at}
              onDismiss={() => handleDismiss(a.id)}
              onEdit={() => { setEditing(a); setComposing(true); }}
              onRetract={() => handleRetract(a.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ComposeCard ──────────────────────────────────────────────────

function ComposeCard({ leagueId, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [title,    setTitle]    = useState(existing?.title || '');
  const [body,     setBody]     = useState(existing?.body || '');
  const [severity, setSeverity] = useState(existing?.severity || 'info');
  const [pinnedUntil, setPinnedUntil] = useState(existing?.pinned_until || '');
  const [linkUrl,  setLinkUrl]  = useState(existing?.link_url || '');
  const [saving,   setSaving]   = useState(false);

  async function save() {
    if (!title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        body: body || null,
        severity,
        pinned_until: pinnedUntil || null,
        link_url: linkUrl || null,
      };
      if (isEdit) {
        await api.patch(`/leagues/${leagueId}/announcements/${existing.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post(`/leagues/${leagueId}/announcements`, payload);
        toast.success('Posted');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={composeCard}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{isEdit ? 'Edit announcement' : 'New announcement'}</h3>
        <button onClick={onClose} style={iconBtn} aria-label="Close"><X size={18} /></button>
      </div>

      <label style={fieldLabel}>Title <span style={{ color: '#a8000a' }}>*</span></label>
      <input value={title} onChange={(e) => setTitle(e.target.value)}
             maxLength={160} style={input}
             placeholder="Short summary (max 160 chars)" />

      <label style={fieldLabel}>Body</label>
      <textarea value={body} onChange={(e) => setBody(e.target.value)}
                maxLength={5000} rows={5} style={{ ...input, resize: 'vertical', minHeight: 100 }}
                placeholder="Optional. Markdown is NOT rendered — keep it plaintext." />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 4 }}>
        <div>
          <label style={fieldLabel}>Severity</label>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={input}>
            <option value="info">Info (no push)</option>
            <option value="important">Important (push)</option>
            <option value="urgent">Urgent (push, highlighted)</option>
          </select>
        </div>
        <div>
          <label style={fieldLabel}>Pin until (optional)</label>
          <input type="date" value={pinnedUntil}
                 onChange={(e) => setPinnedUntil(e.target.value)} style={input} />
        </div>
      </div>

      <label style={fieldLabel}>Link URL (optional)</label>
      <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
             maxLength={1000} style={input}
             placeholder="https://…" />

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <button onClick={onClose} style={secondaryBtn} disabled={saving}>Cancel</button>
        <button onClick={save} style={primaryBtn} disabled={saving || !title.trim()}>
          {saving ? 'Saving…' : (isEdit ? 'Update' : 'Post')}
        </button>
      </div>
    </div>
  );
}

// ─── AnnouncementCard ─────────────────────────────────────────────

function AnnouncementCard({ announcement: a, canEdit, onDismiss, onEdit, onRetract }) {
  const severityStyle = sevStyle(a.severity);
  const isRetracted = !!a.retracted_at;

  return (
    <article style={{
      ...cardBase,
      ...(isRetracted ? cardRetracted : {}),
      borderLeft: `4px solid ${severityStyle.accent}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {a.is_pinned && (
              <span style={pinBadge}><Pin size={12} /> Pinned</span>
            )}
            <span style={{ ...sevBadge, background: severityStyle.bg, color: severityStyle.fg }}>
              {a.severity}
            </span>
            {isRetracted && <span style={retractedBadge}>RETRACTED</span>}
          </div>
          <h3 style={{ margin: '4px 0 6px', fontSize: 17, fontWeight: 700 }}>
            {a.title}
          </h3>
          {a.body && (
            <p style={{ margin: 0, color: '#1a2240', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {a.body}
            </p>
          )}
          {a.link_url && (
            <a href={a.link_url} target="_blank" rel="noopener noreferrer"
               style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, color: '#00a8cc', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Open link <ExternalLink size={14} />
            </a>
          )}
          <footer style={{ marginTop: 12, fontSize: 12, color: '#7a82a0', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>by {a.posted_by?.name || '(unknown)'}</span>
            <span>{fmtTimestamp(a.created_at)}</span>
            {a.pinned_until && a.is_pinned && (
              <span>· pinned until {fmtDate(a.pinned_until)}</span>
            )}
          </footer>
        </div>

        {!isRetracted && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {canEdit && (
              <>
                <button onClick={onEdit} style={iconBtn} title="Edit"><Pencil size={16} /></button>
                <button onClick={onRetract} style={iconBtn} title="Retract"><Trash2 size={16} /></button>
              </>
            )}
            {!a.is_dismissed && (
              <button onClick={onDismiss} style={iconBtn} title="Mark as read"><Check size={16} /></button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

// ─── helpers ──────────────────────────────────────────────────────

function fmtTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function sevStyle(s) {
  if (s === 'urgent')    return { bg: 'rgba(255,80,80,0.13)',  fg: '#a8000a',  accent: '#ff5050' };
  if (s === 'important') return { bg: 'rgba(0,184,224,0.13)',  fg: '#005a78',  accent: '#00b8e0' };
  return                       { bg: 'rgba(122,130,160,0.15)', fg: '#4a526e',  accent: '#c8cee0' };
}

// ─── styles ───────────────────────────────────────────────────────

const muted = { color: '#7a82a0', textAlign: 'center', padding: 40 };
const cardBase = {
  background: '#ffffff',
  border: '1px solid #e2e6f0',
  borderRadius: 10,
  padding: 16,
};
const cardRetracted = { opacity: 0.55, background: '#f8f9fb' };
const composeCard = {
  ...cardBase,
  border: '1.5px solid #00d4ff',
  marginBottom: 16,
};
const fieldLabel = { display: 'block', fontSize: 11, color: '#7a82a0', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginTop: 10, marginBottom: 4 };
const input = {
  width: '100%', padding: '8px 12px', fontSize: 14,
  border: '1.5px solid #e2e6f0', borderRadius: 8,
  background: '#ffffff', boxSizing: 'border-box',
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', fontSize: 14, fontWeight: 700,
  background: '#00d4ff', color: '#002430',
  border: 'none', borderRadius: 8, cursor: 'pointer',
};
const secondaryBtn = {
  padding: '8px 16px', fontSize: 14, fontWeight: 700,
  background: '#ffffff', color: '#0c1129',
  border: '1.5px solid #e2e6f0', borderRadius: 8, cursor: 'pointer',
};
const iconBtn = {
  width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: '#ffffff', color: '#4a526e',
  border: '1px solid #e2e6f0', borderRadius: 8, cursor: 'pointer',
};
const pinBadge = {
  display: 'inline-flex', alignItems: 'center', gap: 3,
  padding: '2px 8px', borderRadius: 4,
  background: 'rgba(0,212,255,0.13)', color: '#005a78',
  fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
};
const sevBadge = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 4,
  fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
};
const retractedBadge = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 4,
  background: '#3a4555', color: '#ffffff',
  fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
};
